import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatRelative } from "../lib/api";
import AppShell from "../components/AppShell";
import ShareBar from "../components/ShareBar";
import { useAuth } from "../lib/auth";
import {
  Microphone, ArrowLeft, Play, Pause, Clock, ChatCircleText, Robot, Sparkle,
  Upload, Question, CheckCircle, XCircle, Lightning, Quotes,
} from "@phosphor-icons/react";

const PILLAR_LABELS = {
  aerospace: { name: "Aerospace Integration", color: "#00E5FF" },
  ev: { name: "EV Intelligence", color: "#00E676" },
  urban: { name: "Urban Mobility Systems", color: "#A78BFA" },
  regulatory: { name: "Regulatory Horizon", color: "#FFAB00" },
};

function formatTime(sec) {
  if (!sec || isNaN(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Lightweight markdown renderer (headings/bullets/bold) */
function MarkdownLite({ body }) {
  if (!body) return null;
  const lines = body.split("\n");
  return (
    <div className="prose-mobility text-zinc-200 leading-relaxed">
      {lines.map((ln, i) => {
        if (/^### /.test(ln)) return <h3 key={i} className="text-white font-display mt-6 mb-2" style={{ fontSize: 16, fontWeight: 700 }}>{ln.replace(/^### /, "")}</h3>;
        if (/^## /.test(ln)) return <h2 key={i} className="text-white font-display mt-8 mb-3" style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.015em" }}>{ln.replace(/^## /, "")}</h2>;
        if (/^# /.test(ln)) return <h2 key={i} className="text-white font-display mt-8 mb-3" style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em" }}>{ln.replace(/^# /, "")}</h2>;
        if (/^[-*] /.test(ln)) return <li key={i} className="list-disc ml-6 my-1">{renderInline(ln.replace(/^[-*] /, ""))}</li>;
        if (/^\d+\. /.test(ln)) return <li key={i} className="list-decimal ml-6 my-1">{renderInline(ln.replace(/^\d+\. /, ""))}</li>;
        if (ln.trim() === "") return <div key={i} className="h-3" />;
        return <p key={i} className="my-2">{renderInline(ln)}</p>;
      })}
    </div>
  );
}
function renderInline(text) {
  const parts = [];
  let i = 0;
  const re = /\*\*([^*]+)\*\*/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > i) parts.push(text.slice(i, m.index));
    parts.push(<strong key={m.index} className="text-white">{m[1]}</strong>);
    i = m.index + m[0].length;
  }
  if (i < text.length) parts.push(text.slice(i));
  return parts;
}

function AudioPlayer({ src, onPlayUpdate, onComplete }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    setPlaying(false);
    setCurrent(0);
  }, [src]);

  const toggle = () => {
    if (!ref.current) return;
    if (playing) ref.current.pause();
    else ref.current.play();
  };

  const seek = (e) => {
    if (!ref.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    ref.current.currentTime = pct * duration;
  };

  if (!src) {
    return (
      <div className="card p-5 text-center text-zinc-500 text-sm" data-testid="audio-player-empty">
        Audio is generating — refresh in a minute or read the show notes below.
      </div>
    );
  }

  return (
    <div className="card p-5" data-testid="audio-player">
      <audio
        ref={ref}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onTimeUpdate={(e) => {
          setCurrent(e.target.currentTime);
          if (onPlayUpdate) onPlayUpdate(Math.floor(e.target.currentTime));
        }}
        onEnded={() => { setPlaying(false); if (onComplete) onComplete(); }}
      />
      <div className="flex items-center gap-4">
        <button
          onClick={toggle}
          className="w-12 h-12 rounded-full bg-[#E60012] hover:bg-[#E60012] flex items-center justify-center transition-transform hover:scale-105 shrink-0"
          data-testid="audio-player-toggle"
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause size={18} weight="fill" className="text-white" /> : <Play size={18} weight="fill" className="text-white ml-0.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <div
            onClick={seek}
            className="h-1.5 bg-[#1A1A1D] cursor-pointer relative overflow-hidden"
            data-testid="audio-player-seekbar"
          >
            <div className="h-full" style={{ width: `${duration ? (current / duration) * 100 : 0}%`, background: "#E60012" }} />
          </div>
          <div className="flex justify-between mt-1.5 label-mono">
            <span>{formatTime(current)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PodcastDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [ep, setEp] = useState(null);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [related, setRelated] = useState([]);
  const [clipNote, setClipNote] = useState("");
  const [clipBusy, setClipBusy] = useState(false);
  const [clipStatus, setClipStatus] = useState(null);
  const fileInput = useRef(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  // quiz state
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/podcasts/${slug}`);
    setEp(data);
    const { data: more } = await api.get(`/podcasts`, { params: { pillar: data.pillar, limit: 4 } });
    setRelated(more.filter((p) => p.slug !== data.slug).slice(0, 3));
  };

  useEffect(() => { load().catch(() => {}); }, [slug]); // eslint-disable-line

  const sendToAgent = async () => {
    if (!user) return;
    setClipBusy(true);
    setClipStatus(null);
    try {
      const { data } = await api.post(`/podcasts/${slug}/clip-to-agent`, {
        start_sec: 0,
        end_sec: ep?.duration_sec || 0,
        note: clipNote.trim(),
      });
      setClipStatus({ ok: true, agent_name: data.agent_name });
      setClipNote("");
    } catch (_) {
      setClipStatus({ ok: false });
    } finally {
      setClipBusy(false);
    }
  };

  const onUploadAudio = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadBusy(true);
    try {
      const form = new FormData();
      form.append("audio", file);
      await api.post(`/podcasts/${slug}/upload-audio`, form, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (_) {}
    finally { setUploadBusy(false); if (fileInput.current) fileInput.current.value = ""; }
  };

  const recordPlay = async (sec) => {
    if (!user || sec % 15 !== 0) return; // ping every 15s
    try { await api.post(`/podcasts/${slug}/play`, { played_sec: sec }); } catch (_) {}
  };
  const recordComplete = async () => {
    if (!user) return;
    try { await api.post(`/podcasts/${slug}/play`, { completed: true }); } catch (_) {}
  };

  if (!ep) return <AppShell><div className="py-20 text-center label-mono"><span className="spinner mr-2 align-middle" />LOADING EPISODE...</div></AppShell>;

  const pillar = PILLAR_LABELS[ep.pillar] || { name: ep.pillar, color: "#E60012" };
  const cover = ep.cover_image ? `${process.env.REACT_APP_BACKEND_URL}${ep.cover_image}` : "";
  const audioSrc = ep.audio_url ? `${process.env.REACT_APP_BACKEND_URL}${ep.audio_url}` : "";

  const quizScore = (() => {
    if (!ep.quiz || !ep.quiz.length) return null;
    let correct = 0;
    ep.quiz.forEach((q, i) => { if (quizAnswers[i] === q.answer_index) correct += 1; });
    return { correct, total: ep.quiz.length };
  })();

  return (
    <AppShell>
      <div className="grid lg:grid-cols-12 gap-8" data-testid="podcast-detail-page">
        <article className="lg:col-span-8">
          <Link to="/podcasts" className="btn btn-ghost btn-sm mb-6" data-testid="podcast-back">
            <ArrowLeft size={12} weight="bold" /> All episodes
          </Link>

          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="hub-dot" style={{ color: pillar.color }} />
            <span className="label-mono" style={{ color: pillar.color }}>{pillar.name.toUpperCase()}</span>
            <span className="label-mono">·</span>
            <span className="label-mono">{ep.episode_type.toUpperCase()}</span>
            <span className="label-mono">·</span>
            <span className="label-mono flex items-center gap-1"><Clock size={10} /> {formatTime(ep.duration_sec)}</span>
            {!ep.audio_is_ai && (
              <span className="label-mono px-2 py-0.5 border" style={{ color: "#00E676", borderColor: "#00E676" }}>STUDIO AUDIO</span>
            )}
          </div>

          <h1 className="font-display text-white mb-3" style={{ fontSize: "clamp(28px, 4vw, 48px)", fontWeight: 900, letterSpacing: "-0.035em", lineHeight: 1.05 }} data-testid="podcast-title">
            {ep.title}
          </h1>
          <p className="text-lg text-zinc-300 leading-relaxed mb-8">{ep.summary}</p>

          {cover && (
            <div className="border border-[#27272A] mb-6 bg-black overflow-hidden">
              <img src={cover} alt="" className="w-full max-h-[420px] object-cover" />
            </div>
          )}

          <div className="mb-8">
            <AudioPlayer src={audioSrc} onPlayUpdate={recordPlay} onComplete={recordComplete} />
          </div>

          {ep.show_notes_md && (
            <section className="mb-10" data-testid="podcast-show-notes">
              <h2 className="font-display text-white text-2xl mb-3" style={{ fontWeight: 700, letterSpacing: "-0.02em" }}>Show notes</h2>
              <MarkdownLite body={ep.show_notes_md} />
            </section>
          )}

          {ep.transcript_md && (
            <section className="mb-10">
              <button
                onClick={() => setTranscriptOpen((v) => !v)}
                className="btn btn-ghost btn-sm mb-4"
                data-testid="podcast-transcript-toggle"
              >
                <Quotes size={12} weight="bold" /> {transcriptOpen ? "Hide" : "Show"} transcript
              </button>
              {transcriptOpen && (
                <div data-testid="podcast-transcript">
                  <MarkdownLite body={ep.transcript_md} />
                </div>
              )}
            </section>
          )}

          {ep.quiz && ep.quiz.length > 0 && (
            <section className="mb-10" data-testid="podcast-quiz">
              <div className="flex items-center gap-2 mb-4">
                <Question size={16} weight="bold" style={{ color: "#E60012" }} />
                <h2 className="font-display text-white text-2xl" style={{ fontWeight: 700, letterSpacing: "-0.02em" }}>Lock it in — quiz</h2>
              </div>
              <div className="space-y-4">
                {ep.quiz.map((q, qi) => {
                  const picked = quizAnswers[qi];
                  return (
                    <div key={qi} className="card p-4" data-testid={`quiz-item-${qi}`}>
                      <p className="text-white mb-3"><strong>{qi + 1}.</strong> {q.question}</p>
                      <ul className="space-y-2">
                        {q.choices.map((c, ci) => {
                          const isPicked = picked === ci;
                          const isCorrect = q.answer_index === ci;
                          const reveal = quizSubmitted;
                          const cls = reveal
                            ? (isCorrect ? "border-[#00E676] text-white" : (isPicked ? "border-[#E60012] text-zinc-400 line-through" : "border-[#27272A] text-zinc-400"))
                            : (isPicked ? "border-white text-white" : "border-[#27272A] text-zinc-300 hover:text-white");
                          return (
                            <li key={ci}>
                              <button
                                disabled={quizSubmitted}
                                onClick={() => setQuizAnswers((m) => ({ ...m, [qi]: ci }))}
                                className={`w-full text-left px-3 py-2 border flex items-center gap-2 ${cls}`}
                                data-testid={`quiz-choice-${qi}-${ci}`}
                              >
                                {reveal && isCorrect && <CheckCircle size={12} weight="fill" style={{ color: "#00E676" }} />}
                                {reveal && isPicked && !isCorrect && <XCircle size={12} weight="fill" style={{ color: "#E60012" }} />}
                                <span>{c}</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                      {quizSubmitted && q.rationale && (
                        <p className="text-sm text-zinc-400 mt-3 border-t border-dashed border-[#27272A] pt-3">
                          <strong className="text-zinc-300">Why:</strong> {q.rationale}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mt-4">
                {!quizSubmitted ? (
                  <button
                    onClick={() => setQuizSubmitted(true)}
                    disabled={Object.keys(quizAnswers).length < ep.quiz.length}
                    className="btn btn-primary btn-sm"
                    data-testid="quiz-submit-btn"
                  >
                    <Lightning size={12} weight="fill" /> Submit answers
                  </button>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className="label-mono px-3 py-1.5 border" style={{ color: quizScore.correct === quizScore.total ? "#00E676" : "#FFAB00", borderColor: quizScore.correct === quizScore.total ? "#00E676" : "#FFAB00" }} data-testid="quiz-score">
                      SCORE {quizScore.correct} / {quizScore.total}
                    </span>
                    <button onClick={() => { setQuizSubmitted(false); setQuizAnswers({}); }} className="btn btn-ghost btn-sm">
                      Retake
                    </button>
                  </div>
                )}
              </div>
            </section>
          )}

          <div className="mb-8" data-testid="podcast-share">
            <ShareBar title={ep.title} summary={ep.summary} />
          </div>
        </article>

        <aside className="lg:col-span-4 space-y-6">
          {/* Send to mobility agent */}
          <div className="card p-5" data-testid="send-to-agent-card">
            <div className="label-mono mb-3 flex items-center gap-1.5" style={{ color: "#E60012" }}>
              <Robot size={11} weight="fill" /> SEND TO MOBILITY AGENT
            </div>
            <p className="text-xs text-zinc-400 mb-3">
              Pipe this episode into the linked AI agent and continue the conversation in the chat widget.
            </p>
            <textarea
              value={clipNote}
              onChange={(e) => setClipNote(e.target.value)}
              placeholder="Optional note: 'What's your take on this?'"
              rows={2}
              className="input resize-none text-sm mb-2"
              data-testid="clip-note-input"
            />
            {user ? (
              <button onClick={sendToAgent} disabled={clipBusy} className="btn btn-primary btn-sm w-full" data-testid="send-to-agent-btn">
                {clipBusy ? <span className="spinner" /> : <><ChatCircleText size={12} weight="fill" /> Pipe to agent</>}
              </button>
            ) : (
              <Link to="/login" className="btn btn-primary btn-sm w-full">Sign in to chat</Link>
            )}
            {clipStatus?.ok && (
              <p className="text-xs mt-2" style={{ color: "#00E676" }} data-testid="send-to-agent-success">
                ✓ Sent to {clipStatus.agent_name || "agent"}. Open the chat widget bottom-right.
              </p>
            )}
            {clipStatus && !clipStatus.ok && (
              <p className="text-xs mt-2 text-zinc-400">Could not send — try again.</p>
            )}
          </div>

          {/* Admin: upload real audio */}
          {user?.role === "admin" && (
            <div className="card p-5" data-testid="admin-audio-upload">
              <div className="label-mono mb-3 flex items-center gap-1.5" style={{ color: "#FFAB00" }}>
                <Upload size={11} weight="bold" /> ADMIN · REPLACE AUDIO
              </div>
              <p className="text-xs text-zinc-400 mb-3">
                Upload a real recording (mp3, m4a, wav, ogg · max 100 MB) to replace the AI narration.
              </p>
              <input
                ref={fileInput}
                type="file"
                accept="audio/*"
                onChange={onUploadAudio}
                className="hidden"
                data-testid="admin-audio-input"
              />
              <button
                onClick={() => fileInput.current?.click()}
                disabled={uploadBusy}
                className="btn btn-sm w-full"
                data-testid="admin-audio-upload-btn"
              >
                {uploadBusy ? <span className="spinner mr-2" /> : <Upload size={12} weight="bold" />}
                {uploadBusy ? "Uploading..." : "Choose audio file"}
              </button>
            </div>
          )}

          {/* Episode meta */}
          <div className="card p-5" data-testid="podcast-meta">
            <div className="label-mono mb-3">EPISODE</div>
            <ul className="space-y-2 text-sm">
              <li className="flex justify-between"><span className="label-mono">HOST</span><span className="text-white">{ep.host}</span></li>
              {ep.guest_name && (
                <li className="flex justify-between gap-3"><span className="label-mono">GUEST</span><span className="text-white text-right">{ep.guest_name}</span></li>
              )}
              {ep.guest_role && (
                <li className="text-xs text-zinc-400 italic text-right">{ep.guest_role}</li>
              )}
              <li className="flex justify-between"><span className="label-mono">DURATION</span><span className="text-white">{formatTime(ep.duration_sec)}</span></li>
              <li className="flex justify-between"><span className="label-mono">PUBLISHED</span><span className="text-white">{formatRelative(ep.published_at)}</span></li>
              {ep.tags && ep.tags.length > 0 && (
                <li className="pt-2 border-t border-dashed border-[#27272A]">
                  <div className="label-mono mb-2">TAGS</div>
                  <div className="flex flex-wrap gap-1">
                    {ep.tags.map((t) => <span key={t} className="label-mono px-1.5 py-0.5 border border-[#27272A]">{t.toUpperCase()}</span>)}
                  </div>
                </li>
              )}
            </ul>
          </div>

          {/* Related */}
          {related.length > 0 && (
            <div className="card p-5" data-testid="podcast-related">
              <div className="label-mono mb-3 flex items-center gap-1.5" style={{ color: pillar.color }}>
                <Microphone size={11} weight="fill" /> MORE IN {pillar.name.toUpperCase()}
              </div>
              <ul className="space-y-3">
                {related.map((r) => (
                  <li key={r.id}>
                    <Link to={`/podcasts/${r.slug}`} className="block group">
                      <div className="text-sm text-white group-hover:underline underline-offset-4 line-clamp-2 leading-snug mb-1">{r.title}</div>
                      <div className="label-mono">{r.episode_type.toUpperCase()} · {formatTime(r.duration_sec)}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </AppShell>
  );
}
